import { INotificationTreatment } from '@pushly/aqe/lib/interfaces/notification-treatment'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { OrgNotificationModel } from '../models/notification/org-notification.model'
import { generate as generateId } from 'randomstring'
import { INotificationAction } from '@pushly/aqe/lib/interfaces/notification-action'
import { INotificationFieldSuggestions } from '../interfaces/notification-field-suggestions'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { getAltNativeUrl } from '../features/notifications/helpers/notification-helpers'

/**
 * Extracts notification summary content for the given platform.
 * When no platform is provided, then the web template content
 * will be used by default.
 */
export function extractSummaryNotification(
    notification: GetNotificationResponse | OrgNotificationModel,
    platform?: PreviewPlatform,
): INotificationTreatment {
    let id: string | undefined
    let title: string
    let body: string | undefined
    let icon: string | undefined | null
    let image: string | undefined | null
    let badge: string | undefined | null
    let landingUrl: string
    let altNativeUrl: string | undefined | null
    let actions: INotificationAction[] = []
    let keywords: string[] = []
    let subtitle: string | undefined

    // When platform is not defined, web content will be used as the default
    const isPlatformWeb =
        !platform || [PreviewPlatform.MAC_OS, PreviewPlatform.WINDOWS, PreviewPlatform.ANDROID].includes(platform)
    const isPlatformIos = !isPlatformWeb && platform === PreviewPlatform.IOS
    const isPlatformAndroid = !isPlatformWeb && platform === PreviewPlatform.NATIVE_ANDROID

    if (notification instanceof OrgNotificationModel) {
        const contentWrapper = notification.getVariant()?.getContent()
        const defaultContent = contentWrapper?.getDefaultContent()
        const platformContent =
            (isPlatformWeb
                ? contentWrapper?.getWebContent()
                : isPlatformIos
                ? contentWrapper?.getNativeIosContent()
                : isPlatformAndroid
                ? contentWrapper?.getNativeAndroidContent()
                : defaultContent) ?? defaultContent

        id = notification.getId()?.toString()
        title = platformContent?.getTitle() ?? defaultContent?.getTitle() ?? ''
        body = platformContent?.getBody() ?? defaultContent?.getBody() ?? ''
        icon = platformContent?.getIconUrl() ?? defaultContent?.getIconUrl()
        image = platformContent?.getImageUrl() ?? defaultContent?.getImageUrl()
        landingUrl =
            (platformContent && 'getLandingUrl' in platformContent
                ? platformContent.getLandingUrl()
                : defaultContent?.getLandingUrl()) ??
            defaultContent?.getLandingUrl() ??
            ''
        altNativeUrl = defaultContent?.getNativeAltUrl()

        if (platformContent && 'getSubtitle' in platformContent) {
            subtitle = platformContent.getSubtitle()
        }

        badge = defaultContent?.getBadgeUrl()
        actions = defaultContent?.getActions()?.map((a) => a.serialize()) ?? []
        keywords = defaultContent?.getKeywords() ?? []
    } else {
        const defaultContent = notification.template?.channels?.default
        const platformContent =
            (isPlatformWeb
                ? notification.template?.channels?.web
                : isPlatformIos
                ? notification.template?.channels?.native_ios
                : isPlatformAndroid
                ? notification.template?.channels?.native_android
                : defaultContent) ?? defaultContent

        // Helper: read a field from the platform channel, falling back to the default channel.
        // Handles fields that only exist on some channel types (badge_url, subtitle, actions).
        const field = <K extends string>(key: K): any =>
            (platformContent && key in platformContent ? (platformContent as any)[key] : undefined) ??
            (defaultContent as any)?.[key]

        id = notification.id?.toString()
        title = field('title') ?? ''
        body = field('body') ?? ''
        icon = field('icon_url')
        image = field('image_url')
        landingUrl = field('landing_url') ?? ''
        altNativeUrl = getAltNativeUrl(notification.template?.channels)
        subtitle = field('subtitle') ?? undefined
        badge = field('badge_url')
        actions = field('actions') ?? []
        keywords = notification.template?.keywords ?? []
    }

    return {
        id: id ?? generateId({ length: 5 }),
        title,
        subtitle,
        body,
        image: image ?? undefined,
        icon: icon ?? undefined,
        badge: badge ?? undefined,
        altNativeUrl: altNativeUrl ?? undefined,
        landingUrl,
        landingUrlDisabled: !landingUrl,
        actions,
        keywords,
    }
}

/**
 * Extracts AI content metadata from autoSuggestedFieldsResults into
 * the shape expected by NotificationSummaryCard's aiFields prop.
 *
 * - Returns undefined when no AI source can be determined
 * - Infers 'page_analyzer' when no explicit source but content fields exist (legacy fallback)
 * - Prefers sourceMetadata.tone/reason over deprecated top-level fields
 * - Does not mutate the input
 */
export function extractAiFields(
    results?: INotificationFieldSuggestions | null,
): { contentSource: string; edited?: boolean; tone?: string; reason?: string } | undefined {
    if (!results) return undefined

    let source = results.source
    // Legacy fallback: infer page_analyzer from content presence
    if (
        !source &&
        (results.title || results.body || results.imageUrl || (results as any).image_url || results.keywords?.length)
    ) {
        source = 'page_analyzer'
    }

    if (!source) return undefined

    return {
        contentSource: source,
        edited: (results as any).edited ?? undefined,
        tone: (results as any).source_metadata?.tone ?? results.sourceMetadata?.tone ?? results.tone ?? undefined,
        reason:
            (results as any).source_metadata?.reason ?? results.sourceMetadata?.reason ?? results.reason ?? undefined,
    }
}
